import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SentimentOscillatorChart } from "@/cockpit/talk/SentimentOscillatorChart";
import { SentimentFeedTable } from "@/cockpit/talk/SentimentFeedTable";
import { SentimentAdvanceDeclineChart } from "@/cockpit/talk/SentimentAdvanceDeclineChart";
import { SentimentHeatmapTiles } from "@/cockpit/talk/SentimentHeatmapTiles";
import { SectorHeatmapGrid } from "@/cockpit/SectorHeatmapGrid";
import { CockpitDrawer, RawTable } from "@/cockpit/CockpitDrawer";
import { CockpitPanel, useCockpitAdmin } from "@/cockpit/CockpitPanel";

export function TickerTalkCockpit({ data }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const showMethod = useCockpitAdmin();
  const [open, setOpen] = useState(false);
  const talk = data?.talk;
  const oscRows = useMemo(() => talk?.sentimentOscillator || [], [talk]);
  const heat = talk?.sectorHeatmap || [];
  const ad = talk?.advanceDecline || [];
  const hasSs = oscRows.length > 0 || heat.length > 0 || ad.length > 0 || talk?.sectorComparison;

  return (
    <div className="space-y-3" data-testid="ticker-talk-cockpit">
      {talk?.sectorComparison ? <SentimentHeatmapTiles comparison={talk.sectorComparison} /> : null}
      {heat.length > 0 ? (
        <CockpitPanel
          title="GICS sector heatmap"
          hint="11 sectors ranked by 7d sentiment. Not a FinancialData.net house-feed field."
          testid="talk-sector-heatmap"
        >
          <SectorHeatmapGrid items={heat} testid="talk-sector-heatmap-grid" />
        </CockpitPanel>
      ) : null}
      {ad.length > 0 ? <SentimentAdvanceDeclineChart series={ad} /> : null}
      {oscRows.length > 0 ? (
        <SentimentOscillatorChart series={oscRows} onOpenRaw={isAdmin ? () => setOpen(true) : undefined} />
      ) : null}
      {!hasSs && showMethod ? (
        <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="talk-sentisense-deferred">
          Sector heatmap, oscillator, and advance/decline are SentiSense-only fields. They are not shown from mock data. House-feed headlines below are FinancialData.net from Mongo.
        </p>
      ) : null}
      <SentimentFeedTable feed={talk?.feed} houseFeed />
      <CockpitDrawer
        open={open}
        onOpenChange={setOpen}
        title="Oscillator raw series"
        description="Sentiment oscillator series when a SentiSense layer is opted in."
        testid="talk-raw-drawer"
      >
        <RawTable
          columns={[
            { key: "date", label: "Date" },
            { key: "sentimentScore", label: "Score", align: "right" },
            { key: "mentionVolume", label: "Mentions", align: "right" },
            { key: "stockPrice", label: "Close", align: "right", render: (r) => (r.stockPrice != null ? `$${r.stockPrice}` : "—") },
          ]}
          rows={oscRows.slice(-90)}
        />
      </CockpitDrawer>
    </div>
  );
}
